import { Point } from "../models/Point";

class RotationParams {
    outbounding: SVGPathElement;
    arc: SVGPathElement;
    usePart: SVGUseElement;

    constructor(_outbounding: SVGPathElement, _arc: SVGPathElement, _usePart: SVGUseElement) {
        this.outbounding = _outbounding;
        this.arc = _arc;
        this.usePart = _usePart;
    }
}

export class SvgUtils {
    static readonly xmlns: string = "http://www.w3.org/2000/svg";

    static createFlickerAnimation(delay: number = 0, duration: number = 4): SVGAnimateElement {
        const animate = document.createElementNS("http://www.w3.org/2000/svg", "animate");
        animate.setAttribute("attributeName", `opacity`);
        animate.setAttribute("attributeType", `XML`);
        animate.setAttribute("begin", `${delay}s`);
        animate.setAttribute("dur", `${duration}s`);
        animate.setAttribute("keyTimes", `0;0.5;1`);
        animate.setAttribute("repeatCount", `indefinite`);
        animate.setAttribute("type", `rotate`);
        animate.setAttribute("values", `1;0;1`);
        animate.setAttribute("calcMode", `linear`);
        return animate;
    }

    static createLine(x1: number, y1: number, x2: number, y2: number, strokeColor: string, strokeWidth: number): SVGLineElement {
        const line = document.createElementNS("http://www.w3.org/2000/svg", "line");
        line.setAttribute("x1", `${x1}`);
        line.setAttribute("y1", `${y1}`);
        line.setAttribute("x2", `${x2}`);
        line.setAttribute("y2", `${y2}`);
        if (strokeColor && strokeColor.length > 0) line.setAttribute("stroke", strokeColor);
        if (strokeWidth > 0) line.setAttribute("stroke-width", `${strokeWidth}`);
        return line;
    }

    static drawCircle(cx: number, cy: number, radius: number, fillColor: string, strokeColor: string, strokeWidth: number,
        flicker: boolean, delay: number = 0, duration: number = 4): SVGCircleElement {
        const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
        circle.setAttribute("cx", `${cx}`);
        circle.setAttribute("cy", `${cy}`);
        circle.setAttribute("r", `${radius}`);
        if (fillColor && fillColor.length > 0) circle.setAttribute("fill", fillColor);
        if (strokeColor && strokeColor.length > 0) circle.setAttribute("stroke", strokeColor);
        if (strokeWidth > 0) circle.setAttribute("stroke-width", `${strokeWidth}`);
        if (flicker) {
            const flicker = SvgUtils.createFlickerAnimation(delay, duration);
            circle.appendChild(flicker);
        }
        return circle;
    }

    static drawCircle0(id: string, cx: number, cy: number, radius: number): SVGCircleElement {
        const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
        circle.setAttribute("id", id);
        circle.setAttribute("cx", `${cx}`);
        circle.setAttribute("cy", `${cy}`);
        circle.setAttribute("r", `${radius}`);
        return circle;
    }

    static useCircle(reuseId: string, rotation: number, cx: number, cy: number, radius: number, fillColor: string, strokeColor: string, strokeWidth: number,
        flicker: boolean, delay: number = 0, duration: number = 4): SVGUseElement {
        const circle = document.createElementNS("http://www.w3.org/2000/svg", "use");
        circle.setAttribute("href", `#${reuseId}`);
        circle.setAttribute("r", `${radius}`);
        circle.setAttribute("transform", `translate(${cx} ${cy}) rotate(${rotation},0,0)`);
        if (fillColor && fillColor.length > 0) circle.setAttribute("fill", fillColor);
        if (strokeColor && strokeColor.length > 0) circle.setAttribute("stroke", strokeColor);
        if (strokeWidth > 0) circle.setAttribute("stroke-width", `${strokeWidth}`);
        if (flicker) {
            const flicker = SvgUtils.createFlickerAnimation(delay, duration);
            circle.appendChild(flicker);
        }
        return circle;
    }

    static drawPolygon(svg: SVGSVGElement, points: Array<Point>, usedInDefs: boolean, fillColor: string, strokeColor: string, strokeWidth: number): SVGPolygonElement {
        const polygon = document.createElementNS("http://www.w3.org/2000/svg", "polygon");
        points.forEach(point => {
            const svgPoint = svg.createSVGPoint();
            svgPoint.x = point.x;
            svgPoint.y = point.y;
            polygon.points.appendItem(svgPoint);
        });

        if (!usedInDefs) {
            if (fillColor && fillColor.length > 0) polygon.setAttribute("fill", fillColor);
            if (strokeColor && strokeColor.length > 0) polygon.setAttribute("stroke", strokeColor);
            if (strokeWidth > 0) polygon.setAttribute("stroke-width", `${strokeWidth}`);
        }
        return polygon;
    }

    static createPolygonPath(id: string, points: Array<Point>, usedInDefs: boolean, fillColor: string, strokeColor: string, strokeWidth: number): SVGPathElement {
        const polygon = document.createElementNS("http://www.w3.org/2000/svg", "path");
        if(id && id.length > 0) polygon.id = id;
        const d = points.map((value) => `${value.x},${value.y}`)
            .join(" ");
        polygon.setAttribute("d", `M${d}z`);

        if (!usedInDefs) {
            if (fillColor && fillColor.length > 0) polygon.setAttribute("fill", fillColor);
            if (strokeColor && strokeColor.length > 0) polygon.setAttribute("stroke", strokeColor);
            if (strokeWidth > 0) polygon.setAttribute("stroke-width", `${strokeWidth}`);
        }
        return polygon;
    }

    static createPath(id: string, dData: string, usedInDefs: boolean, fillColor: string, strokeColor: string, strokeWidth: number): SVGPathElement {
        const polygon = document.createElementNS("http://www.w3.org/2000/svg", "path");
        if(id && id.length > 0) polygon.id = id;
        polygon.setAttribute("d", dData);

        if (!usedInDefs) {
            if (fillColor && fillColor.length > 0) polygon.setAttribute("fill", fillColor);
            if (strokeColor && strokeColor.length > 0) polygon.setAttribute("stroke", strokeColor);
            if (strokeWidth > 0) polygon.setAttribute("stroke-width", `${strokeWidth}`);
        }
        return polygon;
    }

    static createPath2(id: string, dData: string, usedInDefs: boolean, transform: string, fillColor: string, strokeColor: string, strokeWidth: number): SVGPathElement {
        const polygon = document.createElementNS("http://www.w3.org/2000/svg", "path");
        if(id && id.length > 0) polygon.id = id;
        polygon.setAttribute("d", dData);

        if (!usedInDefs) {
            if (fillColor && fillColor.length > 0) polygon.setAttribute("fill", fillColor);
            if (strokeColor && strokeColor.length > 0) polygon.setAttribute("stroke", strokeColor);
            if (strokeWidth > 0) polygon.setAttribute("stroke-width", `${strokeWidth}`);
        }
        if(transform && transform.length > 0) polygon.setAttribute("transform", transform);
        return polygon;
    }

    // ref: https://stackoverflow.com/questions/5737975/circle-drawing-with-svgs-arc-path
    static getDOfPathOfCircle(x: number, y: number, radius: number) {
        const doubleR = radius * 2;
        const d = [
            "M", x, y,
            "m", `${radius}, 0`,
            "a", `${radius},${radius} 0 1,0 ${-doubleR},0`,
            "a", `${radius},${radius} 0 1,0 ${doubleR},0`
        ].join(" ");
        return d;
    }

    static polarToCartesian(centerX: number, centerY: number, radius: number, angleInDegrees: number) {
        const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
        return {
            x: centerX + radius * Math.cos(angleInRadians),
            y: centerY + radius * Math.sin(angleInRadians)
        };
    }

    static describeArc(x: number, y: number, radius: number, startAngle: number, endAngle: number) {
        const start = SvgUtils.polarToCartesian(x, y, radius, endAngle);
        const end = SvgUtils.polarToCartesian(x, y, radius, startAngle);

        const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

        const d = [
            "M", start.x, start.y,
            "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y
        ].join(" ");

        return d;
    }

    static createRotationOnPath(idOfAnimationPath: string) {
        const am = document.createElementNS("http://www.w3.org/2000/svg", "animateMotion");
        am.setAttribute("rotate", `auto`);
        am.setAttribute("dur", `12s`);
        am.setAttribute("repeatCount", `indefinite`);

        const mpath = document.createElementNS("http://www.w3.org/2000/svg", "mpath");
        mpath.setAttribute("href", `#${idOfAnimationPath}`);
        am.appendChild(mpath);

        return am;
    }

    static createCircleWithRotation(idOfAnimationPath: string, x: number, y: number, radius: number,
        strokeColor: string = "#f7f7f7", strokeWidth: number = 2): SVGGElement {
        const group = document.createElementNS("http://www.w3.org/2000/svg", "g");
        const usePart = document.createElementNS("http://www.w3.org/2000/svg", "use");
        usePart.setAttribute("href", `#${idOfAnimationPath}`);
        group.appendChild(usePart);

        const amRotation = SvgUtils.createRotationOnPath(idOfAnimationPath);
        const d = SvgUtils.getDOfPathOfCircle(0, 0, radius);
        const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
        path.setAttribute("stroke", `${strokeColor}`);
        path.setAttribute("stroke-width", `${strokeWidth}`);
        path.setAttribute("d", `${d}`);

        path.appendChild(amRotation);
        group.appendChild(path);
        return group;
    }

    static drawSatellite(idSuffix: string, dx: number, dy: number, angel: number, radius: number, fillColor: string = "none", strokeColor: string = "#f7f7f7", strokeWidth: number = 2): SVGGElement {
        const idOutbounding = `circleOutbounding_${idSuffix}`;
        const change = Math.PI / 180.0;

        const x = radius * Math.sin(angel * change);
        const y = -radius * Math.cos(angel * change);
        const group = SvgUtils.createCircleWithRotation(idOutbounding, x, y, radius, strokeColor, strokeWidth);
        group.setAttribute("transform", `translate(${dx} ${dy})`);

        return group;
    }

    // static drawAnimateOutbounding2(idSuffix: string, x: number, y: number, radius: number, rotate: number, itemsGapArc: number,
    //     strokeColor: string = "lightGray", strokeWidth: number = 2): SVGGElement {//RotationParams { //Array<SVGGElement> { //
    //     const outGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");
    //     const group = document.createElementNS("http://www.w3.org/2000/svg", "g");

    //     const idOutbounding = `circleOutbounding_${idSuffix}`;
    //     const circumference = Math.PI * radius * 2;
    //     const change = Math.PI / 180.0;
    //     const angel1 = rotate + itemsGapArc * 360 / circumference;
    //     const angel2 = angel1 + 120,
    //         angel3 = angel1 + 240;

    //     outGroup.setAttribute("transform", `translate(${x} ${y})`);

    //     // const x1 = radius * Math.sin(angel1 * change);
    //     // const y1 = -radius * Math.cos(angel1 * change);
    //     // const circle1 = SvgUtils.drawCircle(x1, y1, 3, "lightGray", strokeColor, 0, true, 1, 7);
    //     // group.appendChild(circle1);

    //     // const x2 = radius * Math.sin(angel2 * change);
    //     // const y2 = -radius * Math.cos(angel2 * change);
    //     // const circle2 = SvgUtils.drawCircle(x2, y2, 9, "none", strokeColor, 2, true, 0, 12);
    //     // group.appendChild(circle2);

    //     // const x3 = radius * Math.sin(angel3 * change);
    //     // const y3 = -radius * Math.cos(angel3 * change);
    //     // const circle3 = SvgUtils.drawCircle(x3, y3, 6, "lightGray", strokeColor, 0, true, 2, 5);
    //     // group.appendChild(circle3);

    //     const x1 = radius * Math.sin(0 * change);
    //     const y1 = -radius * Math.cos(0 * change);
    //     const circle1 = SvgUtils.drawCircle(x1, y1, 3, "lightGray", strokeColor, 0, true, 1, 7);
    //     group.appendChild(circle1);

    //     const x2 = radius * Math.sin(120 * change);
    //     const y2 = -radius * Math.cos(120 * change);
    //     const circle2 = SvgUtils.drawCircle(x2, y2, 9, "none", strokeColor, 2, true, 0, 12);
    //     group.appendChild(circle2);

    //     const x3 = radius * Math.sin(240 * change);
    //     const y3 = -radius * Math.cos(240 * change);
    //     const circle3 = SvgUtils.drawCircle(x3, y3, 6, "lightGray", strokeColor, 0, true, 2, 5);
    //     group.appendChild(circle3);

    //     const animateTransform = document.createElementNS("http://www.w3.org/2000/svg", "animateTransform");
    //     animateTransform.setAttribute("attributeName", `transform`);
    //     animateTransform.setAttribute("attributeType", `XML`);
    //     animateTransform.setAttribute("dur", `7s`);
    //     animateTransform.setAttribute("keyTimes", `0;0.25;0.5;0.75;1`);
    //     animateTransform.setAttribute("repeatCount", `indefinite`);
    //     animateTransform.setAttribute("type", `rotate`);
    //     animateTransform.setAttribute("values", `0;90;180;270;360`);
    //     animateTransform.setAttribute("calcMode", `linear`);
    //     group.appendChild(animateTransform);

    //     outGroup.appendChild(group);

    //     return outGroup;
    // }

    static drawAnimateOutbounding2(idSuffix: string, x: number, y: number, radius: number,
        color1: string = "lightGray", color2: string = "lightGray", color3: string = "lightGray"): SVGGElement {
        const outGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");
        const group = document.createElementNS("http://www.w3.org/2000/svg", "g");

        const idOutbounding = `circleOutbounding_${idSuffix}`;
        const change = Math.PI / 180.0;

        outGroup.setAttribute("transform", `translate(${x} ${y})`);

        const x1 = radius * Math.sin(0 * change);
        const y1 = -radius * Math.cos(0 * change);
        const circle1 = SvgUtils.drawCircle(x1, y1, 3, color1, "", 0, true, 1, 7);
        group.appendChild(circle1);

        const x2 = radius * Math.sin(120 * change);
        const y2 = -radius * Math.cos(120 * change);
        const circle2 = SvgUtils.drawCircle(x2, y2, 9, "", color2, 2, true, 0, 12);
        group.appendChild(circle2);

        const x3 = radius * Math.sin(240 * change);
        const y3 = -radius * Math.cos(240 * change);
        const circle3 = SvgUtils.drawCircle(x3, y3, 6, color3, "", 0, true, 2, 5);
        group.appendChild(circle3);

        const animateTransform = document.createElementNS("http://www.w3.org/2000/svg", "animateTransform");
        animateTransform.setAttribute("attributeName", `transform`);
        animateTransform.setAttribute("attributeType", `XML`);
        animateTransform.setAttribute("dur", `7s`);
        animateTransform.setAttribute("keyTimes", `0;0.25;0.5;0.75;1`);
        animateTransform.setAttribute("repeatCount", `indefinite`);
        animateTransform.setAttribute("type", `rotate`);
        animateTransform.setAttribute("values", `0;90;180;270;360`);
        animateTransform.setAttribute("calcMode", `linear`);
        group.appendChild(animateTransform);

        outGroup.appendChild(group);

        return outGroup;
    }

    static drawAnimateOutbounding3(idSuffix: string, x: number, y: number, strokeColor: string = "#f7f7f7", scale: number = 1.3) {
        const usePart = document.createElementNS("http://www.w3.org/2000/svg", "use");
        usePart.setAttribute("href", `#circleOutbounding_${idSuffix}`);
        usePart.setAttribute("transform", `translate(${x} ${y}) scale(${scale} ${scale})`);
        usePart.setAttribute("stroke", strokeColor);
        usePart.setAttribute("stroke-width", `1`);

        const animateTransform = document.createElementNS("http://www.w3.org/2000/svg", "animateTransform");
        animateTransform.setAttribute("attributeName", `transform`);
        animateTransform.setAttribute("type", `scale`);
        animateTransform.setAttribute("additive", `sum`);
        animateTransform.setAttribute("from", `0 0`);
        animateTransform.setAttribute("to", `1 1`);
        animateTransform.setAttribute("beg", `0s`);
        animateTransform.setAttribute("dur", `1.2s`);
        animateTransform.setAttribute("repeatCount", `indefinite`);

        usePart.appendChild(animateTransform);
        return usePart;
    }
}